import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { CreditCard, ArrowRight, Shield, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StripeCheckoutButton = ({ 
  planType, 
  planName, 
  planPrice, 
  onSuccess, 
  onCancel,
  className = '' 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useLanguage();
  const { token } = useAuth();

  const handleCheckout = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Get the current origin URL for redirect
      const originUrl = window.location.origin;

      // Create Stripe checkout session
      const response = await axios.post(
        `${API_URL}/api/subscriptions/create-checkout`,
        { 
          plan_type: planType,
          origin_url: originUrl
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('[Stripe] Checkout error:', err);
      const message = err.response?.data?.detail || 'Could not start checkout. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [planType, token]);

  return (
    <div className={`space-y-4 ${className}`}>
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="bg-muted/50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t('billing.plan')}</span>
          <span className="font-medium">{planName}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t('billing.amount')}</span>
          <span className="font-semibold text-lg">{planPrice}</span>
        </div>
      </div>

      <Button
        onClick={handleCheckout}
        disabled={loading}
        className="w-full rounded-lg h-12 text-base font-medium"
        data-testid="stripe-checkout-btn"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('billing.redirecting')}
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {t('billing.proceedToCheckout')}
            <ArrowRight className="w-4 h-4" />
          </span>
        )}
      </Button>

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Shield className="w-3 h-3" />
        <span>{t('billing.secureCheckout')}</span>
      </div>

      {onCancel && (
        <Button
          variant="ghost"
          onClick={onCancel}
          disabled={loading}
          className="w-full"
        >
          {t('common.cancel')}
        </Button>
      )}
    </div>
  );
};

export default StripeCheckoutButton;
